import { isWorkReadBlocked, markWorkDirty } from '@core/session/recovery'
import { sanitizeLayerStyles } from './layerStyles'
import {
  sanitizeAnnotations,
  sanitizeDimensions,
  sanitizeProjWeight,
} from './lib'
import {
  VIEWS2D_STORE_VERSION,
  type SavedView2D,
  type Views2DStoreFile,
} from './views2dTypes'

// ---------------------------------------------------------------------------
// Persistência das vistas 2D gravadas, POR MODELO.
//
// A chave é o SHA-256 dos bytes do IFC (`LoadedModel.sha256`), o mesmo
// identificador que o BCF usa para amarrar viewpoints a um ficheiro sem
// o enviar para lado nenhum. Consequência prática: reabrir o mesmo IFC
// (do disco, da cache, noutra sessão) traz as vistas de volta; abrir
// outro ficheiro não mistura nada.
//
// Tudo aqui é síncrono e puro salvo o acesso ao `Storage`, que é
// injectável para os testes correrem em Node sem DOM. Nada rebenta por
// causa do armazenamento: quota cheia, modo privado ou JSON corrompido
// dão lista vazia / gravação falhada silenciosa, nunca uma excepção que
// suba até ao painel.
// ---------------------------------------------------------------------------

export const VIEWS2D_KEY_PREFIX = 'vn2r.views2d.'

/** Interface mínima do localStorage de que precisamos. */
export interface KeyValueStore {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

export function views2dStorageKey(sha256: string): string {
  return `${VIEWS2D_KEY_PREFIX}${sha256.toLowerCase()}`
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function isSavedView(v: unknown): v is SavedView2D {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (typeof o.id !== 'string' || o.id === '') return false
  if (typeof o.name !== 'string') return false
  const def = o.def as Record<string, unknown> | undefined
  if (!def || typeof def !== 'object') return false
  if (def.kind !== 'plan' && def.kind !== 'section') return false
  for (const axis of ['origin', 'right', 'up'] as const) {
    const a = def[axis]
    if (!Array.isArray(a) || a.length !== 3) return false
    if (!a.every((n) => typeof n === 'number' && Number.isFinite(n))) {
      return false
    }
  }
  if (typeof def.depth !== 'number' || !Number.isFinite(def.depth)) return false
  return true
}

/** Lê as vistas gravadas deste modelo. Devolve [] quando não há nada,
 *  quando o JSON está corrompido ou quando a versão do formato não
 *  bate certo. */
export function loadSavedViews(
  sha256: string,
  store: KeyValueStore | null = defaultStore(),
): SavedView2D[] {
  if (!store || !sha256) return []
  // Enquanto o arranque não decidir (recuperar ou descartar), as vistas
  // gravadas de uma sessão anterior não entram sozinhas.
  if (isWorkReadBlocked()) return []
  let raw: string | null
  try {
    raw = store.getItem(views2dStorageKey(sha256))
  } catch {
    return []
  }
  if (!raw) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!parsed || typeof parsed !== 'object') return []
  const file = parsed as Partial<Views2DStoreFile>
  if (file.version !== VIEWS2D_STORE_VERSION) return []
  if (!Array.isArray(file.views)) return []
  // Filtra entrada a entrada: uma vista estragada não deve levar as
  // boas atrás.
  return file.views.filter(isSavedView).map((v) => {
    const out: SavedView2D = {
      ...v,
      scale: v.scale === 'fit' ? 'fit' : Number(v.scale) || 100,
      paper: v.paper === 'A3' ? 'A3' : 'A4',
      landscape: Boolean(v.landscape),
      savedAt: Number.isFinite(v.savedAt) ? v.savedAt : 0,
    }
    // Campos novos (terceira leva): ausentes nas vistas antigas, e uma
    // cor estragada não pode levar a vista atrás.
    const styles = sanitizeLayerStyles(v.layerStyles)
    if (styles) out.layerStyles = styles
    else delete out.layerStyles
    if (typeof v.fillCuts === 'boolean') out.fillCuts = v.fillCuts
    else delete out.fillCuts
    if (typeof v.smoothArcs === 'boolean') out.smoothArcs = v.smoothArcs
    else delete out.smoothArcs
    // Sexta leva: traço da projecção e linhas ocultas.
    const pw = sanitizeProjWeight(v.projWeight)
    if (pw) out.projWeight = pw
    else delete out.projWeight
    if (typeof v.hiddenLines === 'boolean') out.hiddenLines = v.hiddenLines
    else delete out.hiddenLines
    // Quarta leva: anotações. Mesma regra de sempre, uma etiqueta
    // estragada não leva a vista atrás.
    const notes = sanitizeAnnotations(v.annotations)
    if (notes) out.annotations = notes
    else delete out.annotations
    // Quinta leva: cotas. Mesma regra.
    const dims = sanitizeDimensions(v.dimensions)
    if (dims) out.dimensions = dims
    else delete out.dimensions
    return out
  })
}

/** Grava a lista completa. `true` quando conseguiu escrever. */
export function persistSavedViews(
  sha256: string,
  views: SavedView2D[],
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store || !sha256) return false
  const file: Views2DStoreFile = {
    version: VIEWS2D_STORE_VERSION,
    views,
  }
  try {
    if (views.length === 0) {
      store.removeItem(views2dStorageKey(sha256))
    } else {
      store.setItem(views2dStorageKey(sha256), JSON.stringify(file))
    }
    markWorkDirty()
    return true
  } catch {
    return false
  }
}

// --- Operações de lista (puras: recebem e devolvem listas) -----------------

/** Insere ou substitui pela `id`. A entrada gravada vai sempre para o
 *  topo (ordenação por `savedAt` descendente na UI). */
export function upsertView(
  views: SavedView2D[],
  view: SavedView2D,
): SavedView2D[] {
  const rest = views.filter((v) => v.id !== view.id)
  return [view, ...rest]
}

export function removeView(views: SavedView2D[], id: string): SavedView2D[] {
  return views.filter((v) => v.id !== id)
}

export function renameView(
  views: SavedView2D[],
  id: string,
  name: string,
): SavedView2D[] {
  const clean = name.trim()
  if (!clean) return views
  return views.map((v) =>
    v.id === id ? { ...v, name: clean, def: { ...v.def, name: clean } } : v,
  )
}

/** Nome livre no estilo "Planta Piso 0 (2)": incrementa até não
 *  colidir com nenhum nome já usado. */
export function uniqueViewName(views: SavedView2D[], base: string): string {
  const used = new Set(views.map((v) => v.name))
  if (!used.has(base)) return base
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} (${n})`
    if (!used.has(candidate)) return candidate
  }
  return `${base} (${Date.now()})`
}

export function duplicateView(
  views: SavedView2D[],
  id: string,
  newId: string,
  now = Date.now(),
): SavedView2D[] {
  const src = views.find((v) => v.id === id)
  if (!src) return views
  const name = uniqueViewName(views, src.name)
  const copy: SavedView2D = {
    ...src,
    id: newId,
    name,
    def: { ...src.def, id: newId, name },
    savedAt: now,
  }
  return [copy, ...views]
}

// --- Preferência do snap ---------------------------------------------------
// Não é de nenhuma vista: é de quem desenha. Fica numa chave à parte,
// fora do ficheiro por modelo.

export const VIEWS2D_SNAP_KEY = 'vn2r.views2d.snap'

/** Ligado por omissão: quem mede sobre um desenho quer os cantos. Só um
 *  'off' explícito o desliga. */
export function loadSnapPref(
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return true
  try {
    return store.getItem(VIEWS2D_SNAP_KEY) !== 'off'
  } catch {
    return true
  }
}

export function saveSnapPref(
  on: boolean,
  store: KeyValueStore | null = defaultStore(),
): boolean {
  if (!store) return false
  try {
    store.setItem(VIEWS2D_SNAP_KEY, on ? 'on' : 'off')
    return true
  } catch {
    return false
  }
}

/** Identificador estável e único o suficiente para uma lista local. */
export function newViewId(now = Date.now(), rand = Math.random()): string {
  return `v2d_${now.toString(36)}_${Math.floor(rand * 1e6).toString(36)}`
}

/** Nome de ficheiro seguro a partir do nome da vista. Sem acentos
 *  perdidos (mantém-se o que o sistema de ficheiros aceita), sem
 *  separadores de caminho, sem espaços a mais. */
export function sanitizeFileName(name: string, fallback = 'vista'): string {
  const cleaned = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._ -]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, 80)
  return cleaned || fallback
}
